import { Navigate, Outlet, useParams } from "react-router";
import { useIngredientContext } from "./IngredientDetails/useIngredientContext";
import { IngredientContextProvider } from "./IngredientDetails/ExistingIngredientContext";
import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { INGREDIENT, useFoodpilotContext, VIEW_SSQ } from "src/context/FoodpilotContext";
import { Slugs } from "src/context/ProtectedContext";
import { URLHandler } from "src/services/URLHandler";
import { getIngredientQuestionnaireEntryUrl } from "./ingredientQuestionnaireAccess";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { IngredientScoreContextProvider } from "./IngredientDetails/IngredientScoreContextProvider";
import { Loading } from "src/components/utils/Loading";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";

/**
 * NOTE — These components are extracted out of `ingredientRouter.tsx` so the
 * router file only exports a single hook-like factory (`useIngredientRouter`),
 * keeping it compatible with Vite React Fast Refresh's "consistent components
 * exports" rule. Mixing component declarations with a `use*` export in the same
 * module breaks HMR.
 *
 * See https://github.com/vitejs/vite-plugin-react-swc#consistent-components-exports
 */
export const IngredientViewRedirect = () => {
    const { selectedCompany, canUser } = useFoodpilotContext();
    const { ingredient, isReady, answerableSupplierId } = useIngredientContext();

    if (isReady === false) return <Loading />;

    if (ingredient === undefined) return null;
    if (selectedCompany === null) return null;

    const url = getIngredientQuestionnaireEntryUrl({
        companyId: selectedCompany.id,
        ingredientId: ingredient.id,
        questionnaireSupplierId: canUser(VIEW_SSQ, INGREDIENT) ? answerableSupplierId : null,
    });

    return <Navigate to={url} replace />;
};

export const QuestionnaireRedirect = () => {
    const { selectedCompany } = useFoodpilotContext();
    const { ingredient, isReady, questionnaireSupplierId } = useIngredientContext();

    if (isReady === false) return <Loading />;

    if (ingredient === undefined || selectedCompany === null) return null;

    const url = getIngredientQuestionnaireEntryUrl({
        companyId: selectedCompany.id,
        ingredientId: ingredient.id,
        questionnaireSupplierId,
    });

    return <Navigate to={url} replace />;
};

export const QuestionnaireSupplierGuard = () => {
    const { supplierId } = useParams();
    const { selectedCompany } = useFoodpilotContext();
    const { ingredient, isReady, readableSupplierIds } = useIngredientContext();

    if (isReady === false) return <Loading />;

    if (ingredient === undefined || selectedCompany === null) return null;

    if (readableSupplierIds.includes(Number(supplierId)) === false) {
        return <Navigate to={URLHandler.ingredient.scores(selectedCompany.id, ingredient.id)} replace />;
    }

    return <Outlet />;
};

type IngredientActionProtectionProps = {
    slug: Slugs;
};
export const IngredientActionProtection = (props: IngredientActionProtectionProps) => {
    const { slug } = props;
    const { canUser } = useFoodpilotContext();

    if (canUser(slug, INGREDIENT) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

export const IngredientScoresContext = () => {
    return (
        <EntityScoresProvider entity="ingredient">
            <IngredientContextProvider>
                <Outlet />
            </IngredientContextProvider>
        </EntityScoresProvider>
    );
};

export const IngredientScoreDetail = () => {
    const { company } = useCompanyMetadata();
    const { ingredient } = useIngredientContext();
    const { scoreId } = useParams();

    if (scoreId === undefined || ingredient === undefined) {
        return <Loading />;
    }

    return (
        <IngredientScoreContextProvider companyId={company.id} ingredientId={ingredient.id} scoreId={Number(scoreId)}>
            <Outlet />
        </IngredientScoreContextProvider>
    );
};
